import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { dbHelpers } from '../../lib/supabase'
import { aiAnalysis } from '../../lib/ai-analysis'
import SafeIcon from '../../common/SafeIcon'
import AIInsightCard from './AIInsightCard'
import WeeklyReport from './WeeklyReport'
import PsychologyAnalysis from './PsychologyAnalysis'
import * as FiIcons from 'react-icons/fi'

const { FiCpu, FiRefreshCw, FiTrendingUp } = FiIcons

const Insights = () => {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const [trades, setTrades] = useState([])
  const [insights, setInsights] = useState(null)
  const [weeklyReport, setWeeklyReport] = useState(null)
  const [psychologyAnalysis, setPsychologyAnalysis] = useState(null)
  const [loading, setLoading] = useState(true)
  const [analyzing, setAnalyzing] = useState(false)

  useEffect(() => {
    if (user) {
      loadData()
    }
  }, [user])

  const loadData = async () => {
    try {
      setLoading(true)
      const tradesData = await dbHelpers.getUserTrades(user.id, 100)
      setTrades(tradesData)
      if (tradesData.length > 0) {
        await generateInsights(tradesData)
      }
    } catch (error) {
      console.error('Error loading insights data:', error)
    } finally {
      setLoading(false)
    }
  }

  const generateInsights = async (tradesData = trades) => {
    if (tradesData.length === 0) return
    try {
      setAnalyzing(true)
      const aiInsights = await aiAnalysis.analyzeTradingPerformance(tradesData, profile)
      setInsights(aiInsights)
      
      const psychAnalysis = await aiAnalysis.analyzePsychology(tradesData, [])
      setPsychologyAnalysis(psychAnalysis)
      
      const weekEnd = new Date()
      const weekStart = new Date(weekEnd.getTime() - 7 * 24 * 60 * 60 * 1000)
      const report = await aiAnalysis.generateWeeklyReport(tradesData, weekStart, weekEnd)
      setWeeklyReport(report)
    } catch (error) {
      console.error('Error generating insights:', error)
    } finally {
      setAnalyzing(false)
    }
  }

  const handleRefreshInsights = () => {
    generateInsights()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-lux-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
      >
        <header>
          <h1 className="text-4xl font-black tracking-tight text-gray-900 dark:text-white bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-400">
            AI Trading Insights
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-2 font-medium">
            Intelligent analysis of your trading patterns
          </p>
        </header>
        <button 
          onClick={handleRefreshInsights} 
          disabled={analyzing || trades.length === 0}
          className="flex items-center space-x-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white px-8 py-3.5 rounded-2xl font-bold shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 hover:-translate-y-0.5 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <SafeIcon icon={FiRefreshCw} className={`h-4 w-4 ${analyzing ? 'animate-spin' : ''}`} />
          <span>{analyzing ? 'Analyzing...' : 'Refresh AI'}</span>
        </button>
      </motion.div>

      {trades.length === 0 ? (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="lux-card p-12 text-center"
        >
          <div className="max-w-md mx-auto">
            <div className="bg-lux-primary/10 rounded-3xl p-6 w-20 h-20 mx-auto mb-6">
              <SafeIcon icon={FiCpu} className="h-full w-full text-lux-primary" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">No Trading Data</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-8">
              Start adding trades to your journal to receive AI-powered insights and analysis.
            </p>
            <button 
              onClick={() => navigate('/journal', { state: { openForm: true } })}
              className="lux-button-primary px-10"
            >
              Add Your First Trade
            </button>
          </div>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <AIInsightCard title="Performance Analysis" insights={insights} loading={analyzing} icon={FiTrendingUp} />
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <PsychologyAnalysis analysis={psychologyAnalysis} loading={analyzing} />
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="lg:col-span-2"
          >
            <WeeklyReport report={weeklyReport} loading={analyzing} />
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default Insights